// supervisor-loop.ts
//
// One tick of the §6.1 control loop: pick the next ready ticket -> persist
// attempt start + baseline -> run the attempt cycle -> archive forensics
// (contract_error/recovering only, no-op otherwise) -> route the outcome ->
// journal ticket_state_changed + attempt_finished when the ticket reaches a
// terminal state. Never blocks on a long session — `runOneSupervisorTick`
// is one `await` chain per call, so callers (the real daemon in a later
// phase, or this phase's E2E harness) control the poll cadence themselves;
// nothing here does its own `setInterval`/`setTimeout` sleep.
import { randomUUID } from "node:crypto";
import { nextReadyTicket } from "./scheduler.js";
import { runAttemptCycle } from "./supervisor-attempt-cycle.js";
import {
  deadlineRateLimitStreakFor,
  quotaBackoffIndexFor,
  readAttemptStateFile,
  retryStateFor,
  withDeadlineRateLimitStreakAfterRoute,
} from "./supervisor-attempt-state.js";
import { archiveForensicsForVerdict } from "./supervisor-forensics-archival.js";
import { journalAttemptStart } from "./supervisor-loop-attempt-start.js";
import { finalizeAttemptAfterRoute } from "./supervisor-loop-post-route-finalize.js";
import {
  manifestPathsForTicket,
  type SupervisorTickInput,
  type SupervisorTickResult,
} from "./supervisor-loop-types.js";
import { routeAttemptOutcome } from "./supervisor-outcome-router.js";
import { runPreDispatchChecks } from "./supervisor-pre-dispatch-checks.js";

// Types split to supervisor-loop-types.ts (bước 0a, phase 03) — re-exported
// so `import … from "./supervisor-loop.js"` keeps working for every existing
// importer (supervisor-pre-dispatch-checks.ts, supervisor-run-until-idle-
// types.ts, supervisor-candidate-review-dispatch.ts).
export type { SupervisorTickInput, SupervisorTickResult } from "./supervisor-loop-types.js";

/**
 * Runs exactly one attempt for the scheduler's next ready ticket, or returns
 * `idle` when nothing is ready (busy ticket in flight, or DAG exhausted).
 */
export async function runOneSupervisorTick(
  input: SupervisorTickInput,
): Promise<SupervisorTickResult> {
  const now = input.now ?? (() => new Date());
  const ticket = nextReadyTicket(input.projection);
  if (ticket === undefined) return { kind: "idle" };

  const preflight = await runPreDispatchChecks({
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    ticket,
    g2PlanReadiness: input.g2PlanReadiness,
  });
  if (preflight !== undefined) return preflight;

  const attemptState = await readAttemptStateFile(input.controlRoot);
  const priorRetryState = retryStateFor(attemptState, ticket.id);
  const attemptNo = priorRetryState.physical_runs + 1;
  // Ladder position for THIS ticket's next quota sleep, if it hits one —
  // read here (not hardcoded 0) so consecutive quota retries on the same
  // ticket climb backoff_min[1], [2], ... instead of replaying step 0
  // forever (§18/§6.4 "backoff 15→30→60 phút").
  const priorBackoffIndex = quotaBackoffIndexFor(attemptState, ticket.id);

  // D08 (dogfood vòng 4, Bug D fix): run_id + input_hash generated inside
  // journalAttemptStart, before dispatch, so both are journaled on
  // attempt_started itself (supervisor-loop-attempt-start.ts, split —
  // review 260909-p6-m2 INFO).
  const prompt = input.buildPrompt(ticket);
  const attemptStartedAt = now();
  const { runId } = await journalAttemptStart({ tickInput: input, ticket, attemptNo, prompt });

  const cycle = await runAttemptCycle({
    projection: input.projection,
    ticket,
    missionId: input.missionId,
    missionWorkspace: input.missionWorkspace,
    repoRoot: input.repoDir,
    attemptNo,
    runtimeLimitsForKind: input.runtimeLimitsForKind,
    sessionDeps: input.sessionDeps,
    prompt,
    workerModel: input.workerModel,
    runId,
  });

  // MUST run before this function returns — the NEXT tick's
  // `recoverOwnedCheckout` (supervisor-pre-dispatch-checks.ts) would erase
  // the exact diff forensics needs to archive for a contract_error/recovering
  // verdict (§9.4). SOL-05: the CONTROL root — `attempts/` is read there
  // (log retention, orphan reclaim); inside the worktree the next tick's
  // `git clean -fd` erased it and a later bootstrap commit swept it in.
  await archiveForensicsForVerdict({
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    ticketId: ticket.id,
    attemptNo,
    cycle,
  });

  const route = await routeAttemptOutcome({
    cycle,
    retryState: priorRetryState,
    retryConfig: input.retryConfig,
    quotaConfig: input.quotaConfig,
    quotaStatePath: `${input.controlRoot}/quota-sleep.json`,
    // B4: control root, KHÔNG phải missionWorkspace — gate đọc interview.json ở
    // cùng root này (xem `controlRoot` trong supervisor-pre-dispatch-checks.ts).
    // Verifier thì nhận `controlRoot: input.missionWorkspace` — root KHÁC; nhầm
    // hai cái là câu hỏi ghi ra chỗ không ai gác.
    // Không ghi số dòng ở đây: hai số cũ (`:107`, `:167`) đều trỏ sai sau một
    // lần tách file, đúng loại chú thích tự mục ruỗng.
    controlRoot: input.controlRoot,
    backoffIndex: priorBackoffIndex,
    now: now(),
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths: manifestPathsForTicket(ticket),
    missionId: input.missionId,
    runId: cycle.kind === "verified" ? cycle.runId : randomUUID(),
    ticketId: ticket.id,
    ticketType: ticket.kind,
    ticketScope: ticket.slice_id,
    ticketSubject: ticket.title,
    ticketStatus: input.projection.ticketStatus,
    beforeSleep: input.beforeSleep,
    notify: input.notify,
    deadlineRateLimitStreak: deadlineRateLimitStreakFor(attemptState, ticket.id),
  });

  // Post-route: promote review report, persist retry/backoff state, journal
  // attempt_finished, cost accounting — supervisor-loop-post-route-
  // finalize.ts (split, bước 0b phase 03). Same behavior, moved verbatim.
  await finalizeAttemptAfterRoute({
    tickInput: input,
    ticket,
    cycle,
    route,
    attemptState: withDeadlineRateLimitStreakAfterRoute(attemptState, ticket.id, cycle, route),
    attemptNo,
    priorRetryState,
    priorBackoffIndex,
    attemptStartedAt,
  });

  return { kind: "attempted", ticketId: ticket.id, route };
}
